import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Boxes, Globe, Plus, RefreshCw } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Badge } from "~/components/ui/badge";
import { Confirm } from "~/components/ui/confirm";
import { Button } from "~/components/ui/button";
import { DataList, type Column } from "~/components/ui/data-list";
import { Page, PageHeader, Panel, SearchInput, Toolbar } from "~/components/ui/page";
import { Select } from "~/components/ui/select";
import { EmptyState, ErrorState } from "~/components/ui/states";
import { ProductFormSheet } from "~/features/products/product";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey } from "~/lib/errors";
import { listQuery } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { useInfiniteList } from "~/lib/use-list";
import { HelpTip } from "~/components/ui/help-tip";

const search = z.object({
  q: z.string().optional(),
  productType: z.string().optional(),
  missingWeight: z.enum(["1"]).optional(),
  missingCost: z.enum(["1"]).optional(),
  sort: z.enum(["title", "productType", "minPrice", "updatedAt"]).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  new: z.boolean().optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute("/_app/products/")({ validateSearch: search, component: Products });

function fetchProducts(params: Omit<Search, "new"> & { offset: number; limit: number }) {
  return unwrap(api.products.get({ query: listQuery(params) }));
}
type Row = Awaited<ReturnType<typeof fetchProducts>>["items"][number];

const ALL = "__all";

function Products() {
  const t = useT();
  const format = useFormat();
  const can = useCan();
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const { new: creating, ...filters } = Route.useSearch();
  const list = useInfiniteList("products", filters, fetchProducts);
  const types = useQuery({ queryKey: ["products", "types"], queryFn: () => unwrap(api.products.types.get()) });
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const [importing, setImporting] = useState(false);
  const importShopify = useMutation({
    mutationFn: () => unwrap(api.products.import.shopify.post()),
    onSuccess: (result) => {
      setImporting(false);
      void queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success(t("products.importShopifyDone", { created: result.created, updated: result.updated }));
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  const price = (r: Row) => {
    if (r.minPriceCents === null) return "";
    if (r.minPriceCents === r.maxPriceCents) return format.money(r.minPriceCents);
    return t("products.priceFrom", { amount: format.money(r.minPriceCents) });
  };

  const columns: Column<Row>[] = [
    {
      id: "title",
      header: t("products.fields.title"),
      sortKey: "title",
      cell: (r) => (
        <div className="min-w-0 py-1.5">
          <p className="truncate font-medium">{r.title}</p>
          <p className="truncate text-xs text-fg-3 tabular">
            {r.skus.slice(0, 3).join(", ")}
            {r.skus.length > 3 ? "…" : ""}
          </p>
        </div>
      ),
      className: "max-w-[420px]",
    },
    {
      id: "type",
      header: t("products.fields.productType"),
      sortKey: "productType",
      cell: (r) => r.productType ?? "",
      className: "text-fg-2",
    },
    {
      id: "variants",
      header: t("products.fields.variants"),
      align: "end",
      cell: (r) => format.number(r.variantCount),
      minWidth: "lg",
    },
    { id: "price", header: `${t("products.fields.salePrice")}`, sortKey: "minPrice", align: "end", cell: price },
    {
      id: "channel",
      header: t("products.fields.channel"),
      cell: (r) =>
        r.isWeb ? (
          <Badge tone="info">
            <Globe className="size-3" aria-hidden />
            {t("products.channels.web")}
          </Badge>
        ) : (
          <Badge>{t("products.channels.offline")}</Badge>
        ),
      minWidth: "lg",
    },
  ];

  const familyOptions = [
    { value: ALL, label: t("products.allFamilies") },
    ...(types.data ?? []).map((type) => ({ value: type, label: type })),
  ];

  const actions = (
    <>
      {can("imports:run") ? (
        <>
          <span className="inline-flex items-center gap-0.5">
            <Button onClick={() => setImporting(true)} loading={importShopify.isPending}>
              {importShopify.isPending ? null : <RefreshCw />}
              {importShopify.isPending ? t("products.importing") : t("products.importShopify")}
            </Button>
            <HelpTip id="product.importShopify" />
          </span>
          <Confirm
            open={importing}
            onOpenChange={setImporting}
            title={t("confirms.catalogImport.title")}
            body={t("confirms.catalogImport.body")}
            confirmLabel={t("confirms.catalogImport.confirm")}
            loading={importShopify.isPending}
            onConfirm={() => importShopify.mutate()}
          />
        </>
      ) : null}
      {can("products:write") ? (
        <Button variant="primary" onClick={() => setSearch({ new: true })}>
          <Plus />
          {t("products.new")}
        </Button>
      ) : null}
    </>
  );

  return (
    <Page>
      <PageHeader
        title={t("products.title")}
        meta={list.isLoading ? null : t("common.table.rows", { count: list.total })}
        actions={actions}
      />
      <Panel>
        <Toolbar>
          <SearchInput
            value={filters.q ?? ""}
            onChange={(q) => setSearch({ q: q || undefined })}
            placeholder={t("products.searchPlaceholder")}
          />
          <div className="w-full sm:w-56">
            <Select
              aria-label={t("products.fields.productType")}
              value={filters.productType ?? ALL}
              onValueChange={(v) => setSearch({ productType: v === ALL ? undefined : v })}
              options={familyOptions}
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              aria-label={t("products.weightFilter.label")}
              value={filters.missingWeight ?? ALL}
              onValueChange={(v) => setSearch({ missingWeight: v === ALL ? undefined : "1" })}
              options={[
                { value: ALL, label: t("products.weightFilter.all") },
                { value: "1", label: t("products.weightFilter.missing") },
              ]}
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              aria-label={t("products.costFilter.label")}
              value={filters.missingCost ?? ALL}
              onValueChange={(v) => setSearch({ missingCost: v === ALL ? undefined : "1" })}
              options={[
                { value: ALL, label: t("products.costFilter.all") },
                { value: "1", label: t("products.costFilter.missing") },
              ]}
            />
          </div>
        </Toolbar>
        {list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : (
          <DataList
            rows={list.rows}
            rowKey={(r) => r.id}
            columns={columns}
            card={(r) => (
              <>
                <p className="line-clamp-2 font-medium">{r.title}</p>
                <p className="mt-0.5 flex items-center gap-2 text-sm text-fg-2">
                  <span className="tabular">{price(r)}</span>
                  <span className="text-fg-3">{t("products.variantCount", { count: r.variantCount })}</span>
                </p>
              </>
            )}
            onOpen={(r) => void navigate({ to: "/products/$id", params: { id: r.id } })}
            sort={{ key: filters.sort, dir: filters.dir ?? "asc" }}
            onSortChange={(s) => setSearch({ sort: s.key as Search["sort"], dir: s.dir })}
            loading={list.isLoading}
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            onLoadMore={list.loadMore}
            isDimmed={(r) => Boolean(r.archivedAt)}
            empty={
              filters.q || filters.productType || filters.missingWeight || filters.missingCost ? (
                <EmptyState title={t("common.states.noResults")} body={t("common.states.noResultsHint")} />
              ) : (
                <EmptyState icon={<Boxes />} title={t("products.emptyTitle")} body={t("products.emptyBody")} action={actions} />
              )
            }
          />
        )}
      </Panel>
      <ProductFormSheet
        open={Boolean(creating)}
        onOpenChange={(open) => setSearch({ new: open || undefined })}
        onSaved={(p) => void navigate({ to: "/products/$id", params: { id: p.id } })}
      />
    </Page>
  );
}
